import { getJSON, setJSON, removeJSON } from '@/utils/storage/jsonStorage';
import { createScopedLogger } from '@/utils/logging/createScopedLogger';

const log = createScopedLogger('COUNTRY-CACHE');

interface CountryCache {
  countryCode: string;
  countryName: string;
  timestamp: number;
  detectionMethod: 'geoip' | 'fallback';
}

const CACHE_KEY = '@country_cache';
const CACHE_RETENTION_MS = 10 * 60 * 1000;

class PersistentCountryCacheService {
  private cache: CountryCache | null = null;
  private initialized = false;
  private initializationPromise: Promise<void> | null = null;

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this._initializeInternal();
    return this.initializationPromise;
  }

  private async _initializeInternal(): Promise<void> {
    const startTime = Date.now();
    log.info('Initializing persistent country cache...');

    try {
      const parsed = await getJSON<CountryCache>(CACHE_KEY);

      if (parsed) {
        const now = Date.now();
        const age = now - parsed.timestamp;
        const maxAge = CACHE_RETENTION_MS;

        if (age > maxAge) {
          log.info(`Cache expired (age: ${Math.round(age / 1000 / 60)}min), clearing`);
          await this.clear();
        } else {
          this.cache = parsed;
          const duration = Date.now() - startTime;
          log.info(`Loaded country: ${parsed.countryCode} in ${duration}ms`);
        }
      } else {
        log.info('No cached data found, starting fresh');
      }

      this.initialized = true;
    } catch (error) {
      log.error('Error initializing cache:', error);
      this.cache = null;
      this.initialized = true;
    } finally {
      this.initializationPromise = null;
    }
  }

  private async persistCache(): Promise<void> {
    if (!this.cache) {
      return;
    }

    const ok = await setJSON(CACHE_KEY, this.cache);
    if (ok) {
      log.info(`Persisted country: ${this.cache.countryCode}`);
    }
  }

  async get(): Promise<CountryCache | null> {
    await this.initialize();

    if (this.cache) {
      log.debug(`HIT: ${this.cache.countryCode}`);
      return this.cache;
    }

    log.debug('MISS');
    return null;
  }

  async set(
    countryCode: string,
    countryName: string,
    detectionMethod: 'geoip' | 'fallback'
  ): Promise<void> {
    await this.initialize();

    this.cache = {
      countryCode,
      countryName,
      timestamp: Date.now(),
      detectionMethod,
    };

    await this.persistCache();
  }

  async clear(): Promise<void> {
    log.info('Clearing cache');
    this.cache = null;
    this.initialized = false;
    await removeJSON(CACHE_KEY);
  }

  async getCacheStats(): Promise<{
    hasCache: boolean;
    countryCode: string | null;
    age: number | null;
    detectionMethod: string | null;
  }> {
    await this.initialize();

    if (!this.cache) {
      return {
        hasCache: false,
        countryCode: null,
        age: null,
        detectionMethod: null,
      };
    }

    const age = Date.now() - this.cache.timestamp;

    return {
      hasCache: true,
      countryCode: this.cache.countryCode,
      age: Math.round(age / 1000 / 60), // age in minutes
      detectionMethod: this.cache.detectionMethod,
    };
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}

export const persistentCountryCache = new PersistentCountryCacheService();
export type { CountryCache };
